// Google Ads conversion tracking (gtag.js).
//
// The base Google tag also lives on the marketing site (apps/web). Both apps
// share the productlane.com root domain, so an ad click on the marketing site
// is attributed to the conversions we fire here in the app.
//
// Conversion action labels come from Google Ads › Goals › Conversions.
import { isProductionEnvironment } from "@/env"

const GOOGLE_ADS_ID = "AW-16468573728"

const SEND_TO = {
	trialStarted: `${GOOGLE_ADS_ID}/yi91CPf9ybgcEKD86aw9`,
	paidSubscription: `${GOOGLE_ADS_ID}/Iu-yCPr9ybgcEKD86aw9`,
} as const

type Gtag = (...args: unknown[]) => void

function getWindow() {
	return window as unknown as { dataLayer?: unknown[]; gtag?: Gtag }
}

/**
 * Loads the Google Ads gtag.js base tag. Call once on app startup. No-op
 * outside production so dev/staging traffic doesn't pollute conversion data.
 */
export function loadGoogleAdsTag() {
	if (!isProductionEnvironment) return

	const w = getWindow()
	w.dataLayer = w.dataLayer || []
	if (!w.gtag) {
		w.gtag = (...args: unknown[]) => {
			w.dataLayer!.push(args)
		}
	}
	w.gtag("js", new Date())
	w.gtag("config", GOOGLE_ADS_ID)

	const script = document.createElement("script")
	script.async = true
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`
	document.head.appendChild(script)
}

function trackConversion(
	sendTo: string,
	params?: { value?: number; currency?: string },
) {
	const { gtag } = getWindow()
	if (typeof gtag !== "function") return
	gtag("event", "conversion", {
		send_to: sendTo,
		...(params?.value != null ? { value: params.value } : {}),
		...(params?.currency ? { currency: params.currency.toUpperCase() } : {}),
	})
}

/**
 * Fires when a new workspace (7-day trial) is created. Primary bidding signal.
 */
export function trackTrialStartedConversion() {
	trackConversion(SEND_TO.trialStarted)
}

/**
 * Fires when a workspace upgrades to a paid subscription. Secondary signal,
 * used for ROAS reporting. `value` is in major currency units (e.g. euros).
 */
export function trackPaidSubscriptionConversion(params?: {
	value?: number
	currency?: string
}) {
	trackConversion(SEND_TO.paidSubscription, params)
}
